import * as React from "react";
import MUIDataTable, {
  MUIDataTableColumn,
  MUIDataTableOptions,
} from "mui-datatables";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import {
  Autocomplete,
  Button,
  Card,
  Checkbox,
  FormControl,
  Grid,
  Switch,
  TextField,
} from "@mui/material";
import axios from "../../api";
import SoftBox from "components/SoftBox";
import AddCategories from "./AddCategories";
import PageLoader from "examples/ReuseFunctions/PageLoader";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import SoftTypography from "components/SoftTypography";
import { AxiosError } from "axios";
interface AssetType {
  asset_type_id: number;
  asset_type_name: string;
}

interface CategoryData {
  category_id: number;
  name: string;
  asset_type_name: string;
  asset_type_id: number;
  created_at: string;
  is_active: number;
}

interface SelectedRow {
  dataIndex: number;
}

interface TableMeta {
  rowIndex: number;
  columnIndex: number;
  currentTableData: Array<{ index: number; data: unknown[] }>;
  tableData: CategoryData[];
}

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

const tableOptions: MUIDataTableOptions = {
  selectableRows: "none",
  selectableRowsHeader: false,
  elevation: 0,
  tableBodyHeight: "auto",
  responsive: "standard",
};

// Extracted component for checkbox header
interface CheckboxHeaderProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
}

const CheckboxHeader: React.FC<CheckboxHeaderProps> = ({
  checked,
  onChange,
}) => {
  return (
    <th
      style={{
        padding: "16px",
        textAlign: "left",
        backgroundColor: "#f8f9fa",
        borderBottom: "1px solid #e0e0e0",
      }}
    >
      <Checkbox
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
    </th>
  );
};

// Extracted component for checkbox body
interface CheckboxBodyProps {
  checked: boolean;
  onChange: () => void;
}

const CheckboxBody: React.FC<CheckboxBodyProps> = ({ checked, onChange }) => {
  return <Checkbox checked={checked} onChange={onChange} />;
};

// Extracted component for status switch
interface StatusSwitchProps {
  checked: boolean;
  onStatusChange: (isActive: boolean) => void;
}

const StatusSwitch: React.FC<StatusSwitchProps> = ({
  checked,
  onStatusChange,
}) => {
  return (
    <Switch
      checked={checked}
      onChange={(event) => onStatusChange(event.target.checked)}
      color="primary"
    />
  );
};

const CategoriesList: React.FC = () => {
  const [categoryData, setCategoryData] = React.useState<CategoryData[]>([]);
  const [isOpen, setOpen] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [selectedRows, setSelectedRows] = React.useState<SelectedRow[]>([]);
  const [assetTypes, setAssetTypes] = React.useState<AssetType[]>([]);
  const [assetTypeFilter, setAssetTypeFilter] =
    React.useState<AssetType | null>(null);

  const { fetchError, fetchSuccess } = useSnackbar();
  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };
  const handleStatus = async (data: CategoryData, isActive: boolean) => {
    try {
      const response = await axios.put(`/category/${data?.category_id}`, {
        is_active: isActive,
      });
      fetchData();
      fetchSuccess(response.data.message);
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      }
    }
  };
  const formatDate = (dateString: string | number | Date) => {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
    };
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", options).format(date);
  };
  const fetchData = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get("/category", {
        params: {
          filters: { asset_type_id: assetTypeFilter?.asset_type_id },
        },
      });

      setCategoryData(response?.data?.list || []);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error: ", error);
    }
  }, [assetTypeFilter]);

  React.useEffect(() => {
    fetchData();
  }, [assetTypeFilter, fetchData]);

  const handleBulkStatusUpdate = async (isActive: boolean) => {
    try {
      const promises = selectedRows.map(async (row) => {
        const category = categoryData[row.dataIndex];
        return axios.put(`/category/${category.category_id}`, {
          is_active: isActive,
        });
      });
      await Promise.all(promises);
      setSelectedRows([]);
      fetchData();
      fetchSuccess(
        isActive ? "Categories activated" : "Categories deactivated"
      );
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      }
    }
  };
  const handleCheckAllboxes = (checked: boolean) => {
    setSelectedRows(
      checked ? categoryData.map((_, index) => ({ dataIndex: index })) : []
    );
  };

  const handleCheckboxChange = (rowIndex: number) => {
    setSelectedRows((prev) =>
      prev.some((row) => row.dataIndex === rowIndex)
        ? prev.filter((row) => row.dataIndex !== rowIndex)
        : [...prev, { dataIndex: rowIndex }]
    );
  };

  // Fixed: Render functions for custom cells
  const renderCheckboxHeader = () => {
    return (
      <CheckboxHeader
        checked={
          categoryData.length > 0 && selectedRows.length === categoryData.length
        }
        onChange={handleCheckAllboxes}
      />
    );
  };

  const renderCheckboxBody = (_value: unknown, tableMeta: TableMeta) => {
    return (
      <CheckboxBody
        checked={selectedRows.some(
          (row) => row.dataIndex === tableMeta.rowIndex
        )}
        onChange={() => handleCheckboxChange(tableMeta.rowIndex)}
      />
    );
  };

  const renderStatusSwitch = (value: number, tableMeta: TableMeta) => {
    const row =
      tableMeta.tableData[tableMeta.currentTableData[tableMeta.rowIndex].index];
    return (
      <StatusSwitch
        checked={!!value}
        onStatusChange={(isActive) => handleStatus(row, isActive)}
      />
    );
  };

  const columns: MUIDataTableColumn[] = [
    {
      name: "checkbox",
      label: "",
      options: {
        filter: false,
        sort: false,
        customHeadRender: renderCheckboxHeader,
        customBodyRender: renderCheckboxBody,
      },
    },
    { name: "name", label: "Issue" },
    { name: "asset_type_name", label: "Asset Type" },
    {
      name: "created_at",
      label: "Created At",
      options: {
        filter: false,
        customBodyRender: (value: string) => formatDate(value),
      },
    },
    {
      name: "is_active",
      label: "Status",
      options: {
        filter: false,
        customBodyRender: renderStatusSwitch,
      },
    },
  ];
  const fetchAssets = async () => {
    try {
      const response = await axios.get("/asset-types");
      setAssetTypes(response?.data?.list || []);
    } catch (error) {
      console.error("Error: ", error);
    }
  };

  React.useEffect(() => {
    fetchAssets();
  }, []);

  return (
    <>
      <Card
        style={{ padding: "24px", margin: "16px auto", maxWidth: "2000px" }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "16px",
          }}
          className="manage-tickets-header"
        >
          <Grid className="dpflex">
            <FormControl fullWidth>
              <SoftTypography
                variant="caption"
                fontWeight="bold"
                sx={{ paddingBottom: "10px" }}
              >
                Asset Types
              </SoftTypography>
              <Autocomplete
                options={assetTypes}
                value={assetTypeFilter}
                getOptionLabel={(option: AssetType) => option.asset_type_name}
                onChange={(_e, value) => setAssetTypeFilter(value)}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" />
                )}
              />
            </FormControl>
          </Grid>

          <div
            style={{
              display: "block",
              justifyContent: "flex-end",
            }}
          >
            <Button
              className="success-btn"
              color="success"
              onClick={handleOpen}
              sx={{
                marginRight: "10px",
                padding: 2,
                marginBottom: "10px",
                marginTop: "10px",
              }}
            >
              Add Issue Type
            </Button>
            <Button
              className="success-btn"
              onClick={() => handleBulkStatusUpdate(true)}
              disabled={selectedRows.length === 0}
              sx={{
                marginRight: "10px",
                padding: 2,
                marginBottom: "10px",
                marginTop: "10px",
              }}
            >
              Activate
            </Button>
            <Button
              className="success-btn"
              onClick={() => handleBulkStatusUpdate(false)}
              disabled={selectedRows.length === 0}
              sx={{ padding: 2 }}
            >
              InActivate
            </Button>
          </div>
        </div>
        <SoftBox
          sx={{
            "& .MuiTableRow-root:not(:last-child)": {
              "& td": (theme: Theme) => ({
                borderBottom: `${theme.borders.borderWidth[1]} solid ${theme.borders.borderColor}`,
              }),
            },
          }}
          className="manage-tickets m-category"
        >
          {loading ? (
            <PageLoader />
          ) : (
            <ThemeProvider theme={getMuiTheme}>
              <MUIDataTable
                title="Manage Categories"
                data={categoryData}
                columns={columns}
                options={tableOptions}
              />
            </ThemeProvider>
          )}
        </SoftBox>
      </Card>
      {isOpen && (
        <AddCategories
          isOpen={isOpen}
          onClose={handleClose}
          fetchData={fetchData}
        />
      )}
    </>
  );
};

export default CategoriesList;
